'use client';

/**
 * Client-side PostHog adapter.
 *
 * Initialized once from `<PostHogProvider>` in the root layout. Exposes a
 * `trackEvent` / `identifyUser` API for client components (sign-up, sign-in
 * forms, task-form mutations) so we don't have to import posthog-js in
 * every call site.
 *
 * **Local dev is silent** — `initPostHogClient` short-circuits unless
 * `NODE_ENV === 'production'` or `NEXT_PUBLIC_POSTHOG_FORCE_ENABLE === '1'`
 * is set. That keeps the production PostHog project free of dev pageviews,
 * autocaptures, identifies, and exceptions while still letting a developer
 * smoke-test the wiring locally by flipping the flag.
 *
 * Safe-by-default: every helper checks for a running client, so missing
 * the PostHog key in local dev makes them no-ops instead of throwing.
 */

import type { PostHog } from 'posthog-js';
import { getDeploymentEnvironment } from '@/lib/environment';
import { clientLogger } from '@/lib/client-logger';

let client: PostHog | null = null;
let initialized = false;

/**
 * Resolve the browser PostHog project token. Accepts both
 * `NEXT_PUBLIC_POSTHOG_KEY` (the canonical name in PostHog's own docs and
 * the one mainline analytics uses) and `NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN`
 * (alias that pre-existed in this repo) so existing `.env` files keep
 * working either way.
 */
function getClientKey(): string | undefined {
  return (
    process.env.NEXT_PUBLIC_POSTHOG_KEY ??
    process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN
  );
}

function isClientTelemetryEnabled(): boolean {
  if (process.env.NEXT_PUBLIC_POSTHOG_FORCE_ENABLE === '1') return true;
  return process.env.NODE_ENV === 'production';
}

export async function initPostHogClient(): Promise<PostHog | null> {
  if (initialized) return client;
  initialized = true;

  if (typeof window === 'undefined') return null;
  if (!isClientTelemetryEnabled()) return null;

  const key = getClientKey();
  if (!key) return null;

  try {
    const mod = await import('posthog-js');
    const posthog = mod.default;
    posthog.init(key, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST ?? 'https://us.i.posthog.com',
      // We capture pageviews manually from the Next.js App Router so we get
      // the post-navigation URL (the default listener fires on the initial
      // load only inside App Router).
      capture_pageview: false,
      // Autocapture is on by default — that gives PostHog click/form/scroll
      // events for free, which is the main value-add of the hybrid setup.
      autocapture: true,
      // Forward browser-side unhandled errors to PostHog's Errors product
      // automatically. Paired with `app/global-error.tsx` for React render
      // errors that escape route-level error boundaries.
      //
      // De-dup with Sentry: when `NEXT_PUBLIC_SENTRY_DSN` is set, Sentry is the
      // browser exception system-of-record (richer grouping + trace linking),
      // so we turn this OFF to avoid reporting — and double-billing — every
      // error twice. With no Sentry DSN, PostHog keeps capturing so there's
      // never a coverage gap. See `src/instrumentation-client.ts`.
      capture_exceptions: !process.env.NEXT_PUBLIC_SENTRY_DSN,
      // Emit a `$pageleave` event when the user actually leaves the page —
      // tab close, hard refresh, or navigation to a different origin (the
      // SDK listens on `pagehide`/`beforeunload`). Defaults to
      // `'if_capture_pageview'`, which would gate this off since we have
      // `capture_pageview: false`; force it on explicitly so PostHog's
      // session/bounce metrics get accurate exit timestamps. Per-route SPA
      // pageleaves are emitted manually from `<PostHogProvider>` because
      // App Router navigations don't trigger `pagehide`.
      capture_pageleave: true,
      // Stream Core Web Vitals (LCP, CLS, FCP, INP) into PostHog so the
      // Web Vitals dashboard renders. Implemented by posthog-js via the
      // `web-vitals` library; one event per metric per pageview.
      capture_performance: {
        web_vitals: true,
      },
      person_profiles: 'identified_only',
    });
    // Register `environment` as a super property so it rides along on EVERY
    // browser event — autocapture, $pageview, $pageleave, $exception, web
    // vitals, and custom `trackEvent`s — letting the shared PostHog project be
    // filtered by production vs staging (dev). Mirrors the server-side
    // `environment` property and the Logs `deployment.environment` attribute.
    posthog.register({ environment: getDeploymentEnvironment() });
    client = posthog;
    return posthog;
  } catch {
    return null;
  }
}

export function getClient(): PostHog | null {
  return client;
}

/**
 * Capture a product event from a client component. No-op when PostHog hasn't
 * loaded (e.g. local dev without NEXT_PUBLIC_POSTHOG_KEY).
 */
export function trackEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (!client) return;
  try {
    client.capture(event, properties);
  } catch {
    // Telemetry must never break UX.
  }
}

/**
 * Forward a handled error to Sentry (the browser exception system-of-record).
 * This is the client-side analogue of the server `withAction` wrapper
 * (`src/lib/actions/_with-action.ts`): data operations that move client-side
 * (React Query queries/mutations) no longer pass through `withAction`, so
 * their failures are reported here instead. Delegates to the shared
 * `clientLogger`, which routes to `Sentry.captureException` and self-gates
 * (no-op until Sentry has initialized — local dev / SSR / no DSN). PostHog no
 * longer receives errors — it keeps only product analytics.
 */
export function captureException(
  error: unknown,
  properties?: Record<string, unknown>,
): void {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : 'client error';
  clientLogger.error(message, { err: error, ...properties });
}

/**
 * Identify the current user. Call this after signin/signup so subsequent
 * autocapture events join correctly. Pass a Supabase user.id as distinctId.
 */
export function identifyUser(
  distinctId: string,
  properties?: Record<string, unknown>,
): void {
  if (!client || !distinctId) return;
  try {
    client.identify(distinctId, properties);
  } catch {
    // Telemetry must never break UX.
  }
}

/** Clear identity on logout so the next visitor starts as a new person. */
export function resetUser(): void {
  if (!client) return;
  try {
    client.reset();
  } catch {
    // ignore
  }
}

/** Fire a manual pageview — used by the route-change effect in the provider. */
export function capturePageview(url: string): void {
  if (!client) return;
  try {
    client.capture('$pageview', { $current_url: url });
  } catch {
    // ignore
  }
}

/**
 * Fire a manual `$pageleave` for the URL the user is leaving. Called from
 * the route-change effect in `<PostHogProvider>` just before the next
 * `$pageview`, so the SDK has a clean (pageview → pageleave → pageview)
 * stream for each App Router navigation. Hard exits (tab close, cross-
 * origin nav) are handled by the SDK's built-in `pagehide` listener via
 * `capture_pageleave: true` in `initPostHogClient`.
 */
export function capturePageleave(url: string): void {
  if (!client) return;
  try {
    client.capture('$pageleave', { $current_url: url });
  } catch {
    // ignore
  }
}
